import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,

  IconButton,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import logo from '../assets/images/amaze-logo-text.png';

import ThemeToggle from './ThemeToggle';
import '../App.css';

const navLinks = [
  { to: '/', label: 'Home', title: 'Go to the Home Page' },
  { to: '/about', label: 'About Us', title: 'Learn more about Amaze Puzzles' },
  { to: '/products', label: 'Products', title: 'View our Braille puzzles' },
  { to: '/blog', label: 'Blog', title: 'Read our Blog' },
  { to: '/contact', label: 'Contact Us', title: 'Get in touch with us' },
];

const Navbar = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [iconState, setIconState] = useState('hamburger');
  const [elevated, setElevated] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));


  const handleDrawerToggle = () => {
    if (iconState === 'close') {
      setSpinning(true);
      setTimeout(() => {
        setDrawerOpen((v) => !v);
        setIconState((s) => (s === 'hamburger' ? 'close' : 'hamburger'));
        setSpinning(false);
      }, 500);
    } else {
      setDrawerOpen((v) => !v);
      setIconState((s) => (s === 'hamburger' ? 'close' : 'hamburger'));
    }
  };

  useEffect(() => {
    const onScroll = () => setElevated(window.scrollY > 6);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <AppBar
        position="fixed"
        elevation={elevated ? 6 : 0}
        sx={{
          background: 'var(--navbar-gradient)',
          color: 'white',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid var(--navbar-border)',
          transition: 'background-color .25s ease, box-shadow .25s ease, border-color .25s ease',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 64, sm: 72 }, px: { xs: 1.5, sm: 3, md: 4 }, gap: 1 }}>
          {/* Brand (logo) */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              <img src={logo} alt="Amaze Puzzles Logo" style={{ height: 80, marginRight: 12, display: 'block' }} />
            </Link>

            {/* Desktop nav */}
            {!isMobile && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  ml: 1,
                  px: 1,
                  py: 0.75,
                  borderRadius: 9999,
                  border: '1px solid var(--navbar-menu-border)',
                  backgroundColor: 'var(--navbar-menu-bg)',
                  backdropFilter: 'blur(8px)',
                }}
              >
                {navLinks.map(({ to, label, title }) => (
                  <Tooltip key={to} title={title}>
                    <Button
                      component={NavLink}
                      to={to}
                      end={to === '/'}
                      className="nav-item"
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '.9rem',
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 9999,
                        // Active styles via aria-current
                        '&[aria-current="page"]': {
                          color: 'var(--accent)',
                          background: 'var(--navbar-item-active-bg)',
                        },
                        '&[aria-current="page"]::after': {
                          background: 'var(--navbar-item-active-gradient)',
                          content: '""',
                          position: 'absolute',
                          left: 12,
                          right: 12,
                          bottom: 6,
                          height: 2,
                          borderRadius: 2,
                        },
                      }}
                    >
                      {label}
                    </Button>
                  </Tooltip>
                ))}
              </Box>
            )}
          </Box>

          {/* Desktop actions */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ThemeToggle size="medium" />
            </Box>
          )}

          {/* Mobile toggle (your animated hamburger) */}
          {isMobile && (
            <div
              onClick={handleDrawerToggle}
              className={`${iconState === 'close' ? 'close-icon' : 'hamburger-icon'} ${spinning ? 'spin' : ''}`}
              aria-label="Toggle menu"
              aria-expanded={drawerOpen}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleDrawerToggle()}
            >
              <div></div>
              <div></div>
              <div></div>
            </div>
          )}
        </Toolbar>
      </AppBar>

      {/* Spacer so content isn't under the AppBar */}
      <Toolbar sx={{ minHeight: { xs: 64, sm: 72 } }} />

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        sx={{
          '& .MuiDrawer-paper': {
            width: '100%',
            height: '100%',
            backgroundColor: 'var(--background-color) !important',
          },
        }}
      >
        <List sx={{ mt: 4, px: 1 }}>
          {navLinks.map(({ to, label, title }) => (
            <ListItem
              component={NavLink}
              to={to}
              onClick={handleDrawerToggle}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                '&:hover': { backgroundColor: 'var(--navbar-item-hover)' },
                '&[aria-current="page"]': {
                  backgroundColor: 'var(--navbar-item-active-bg)',
                  '&:hover': { backgroundColor: 'var(--navbar-item-active-hover)' },
                },
              }}
            >
              <Tooltip title={title}>
                <ListItemText primary={label} primaryTypographyProps={{ fontWeight: 700 }} />
              </Tooltip>
            </ListItem>
          ))}

          {/* Mobile theme toggle */}
          <ListItem
            sx={{
              borderRadius: 8,
              mt: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              '&:hover': { backgroundColor: 'var(--navbar-item-hover)' },
            }}
          >
            <ListItemText primary="Theme" primaryTypographyProps={{ fontWeight: 700 }} />
            <ThemeToggle size="medium" />
          </ListItem>


        </List>
      </Drawer>
    </>
  );
};

export default Navbar;
